import React, { useState, useEffect } from "react";
import { X, MapPin, Check, Save } from "lucide-react";

export default function InventoryZoneModal({
  isOpen,
  onClose,
  item,
  zones = [],
  onSaveZone,
  isSaving = false
}) {
  const [selectedZone, setSelectedZone] = useState("");
  const [customZone, setCustomZone] = useState("");

  useEffect(() => {
    if (item) {
      setSelectedZone(item.zone || "");
      // If item.zone doesn't match any floorplan zone, prefill customZone
      const matchesFloorplan = zones.some(z => !z.isOutline && z.name === item.zone);
      if (item.zone && !matchesFloorplan) {
        setCustomZone(item.zone);
      } else {
        setCustomZone("");
      }
    }
  }, [item, zones]);

  if (!isOpen || !item) return null;

  const floorplanZones = zones.filter((z) => !z.isOutline);

  const handleSelectPredefined = (zoneName) => {
    setSelectedZone(zoneName);
    setCustomZone("");
  };

  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustomZone(val);
    setSelectedZone(val);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalZone = customZone.trim() || selectedZone.trim();
    onSaveZone(item.id, finalZone);
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
        padding: "1rem"
      }}
      onClick={onClose}
    >
      <div
        className="card glass-card animate-fade-in"
        style={{
          width: "100%",
          maxWidth: "520px",
          backgroundColor: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          overflow: "hidden",
          padding: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{
          padding: "1rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <MapPin size={18} style={{ color: "var(--primary)" }} />
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "var(--text-primary)" }}>
              Change Item Zone Location
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "1.5rem" }}>
          {/* Item Details */}
          <div style={{
            padding: "0.75rem 1rem",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid var(--border-color)",
            borderRadius: "6px",
            marginBottom: "1.25rem"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <span className="sku-badge" style={{ margin: 0 }}>{item.sku}</span>
              <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                Warehouse: <strong style={{ color: "var(--text-primary)" }}>{item.warehouse || "NC"}</strong>
              </span>
            </div>
            <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
              {item.name}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Predefined Floorplan Zones */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label className="form-label" style={{ fontWeight: 600, marginBottom: "0.5rem", display: "block" }}>
                Select Floorplan Zone
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "0.5rem", maxHeight: "180px", overflowY: "auto", paddingRight: "0.25rem" }}>
                {/* Unassigned Option */}
                <button
                  type="button"
                  onClick={() => handleSelectPredefined("")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.5rem 0.75rem",
                    border: !selectedZone && !customZone ? "2px solid var(--primary)" : "1px solid var(--border-color)",
                    borderRadius: "6px",
                    backgroundColor: !selectedZone && !customZone ? "rgba(59, 130, 246, 0.15)" : "rgba(255, 255, 255, 0.02)",
                    color: !selectedZone && !customZone ? "var(--primary)" : "var(--text-secondary)",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: !selectedZone && !customZone ? 600 : 400,
                    textAlign: "left"
                  }}
                >
                  <span>Unassigned</span>
                  {!selectedZone && !customZone && <Check size={14} />}
                </button>

                {floorplanZones.map((z) => {
                  const isSelected = selectedZone === z.name && !customZone;
                  const zoneColor = z.color || "var(--primary)";
                  return (
                    <button
                      key={z.id}
                      type="button"
                      onClick={() => handleSelectPredefined(z.name)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.5rem 0.75rem",
                        border: isSelected ? `2px solid ${zoneColor}` : "1px solid var(--border-color)",
                        borderRadius: "6px",
                        backgroundColor: isSelected ? "rgba(59, 130, 246, 0.15)" : "rgba(255, 255, 255, 0.02)",
                        color: isSelected ? "#ffffff" : "var(--text-primary)",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        fontWeight: isSelected ? 600 : 400,
                        textAlign: "left"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", overflow: "hidden" }}>
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: zoneColor, flexShrink: 0 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{z.name}</span>
                      </div>
                      {isSelected && <Check size={14} style={{ color: zoneColor }} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Zone / Shelf / Racking Input */}
            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <label className="form-label" htmlFor="customZoneInput" style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                Or Enter Custom Shelf / Bin / Location
              </label>
              <input
                id="customZoneInput"
                type="text"
                className="form-input"
                value={customZone}
                onChange={handleCustomChange}
                placeholder="e.g. Rack B4, Aisle 3, Shelf 2..."
                style={{ fontSize: "0.85rem" }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                <Save size={14} /> {isSaving ? "Updating..." : "Save Zone Location"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
